/**
 * What a saved chart *is*, in one line, without fetching anything.
 *
 * A catalog row has to say more than a title, and the honest answer to "what is
 * this chart" already lives in the stored parameters: which ship, over what
 * window, plotting what against what. So the summary is computed from `params`
 * rather than from a rendered preview - it costs nothing, it is searchable, and
 * it cannot be stale the way a thumbnail can.
 *
 * It says which dimensions are pinned. A chart may use its own ships, its own
 * range, both, or neither; the other dimensions follow the viewer.
 *
 * Pure - no atoms are written, and `shipLabel` is the only lookup. Tested as a
 * unit, because it is the row's entire content.
 */

import { describeTable } from '../plot/table-config.ts';
import { describeGauge } from '../plot/gauge-config.ts';
import { describeSankey } from '../plot/sankey-config.ts';
import type { CatalogChart } from '../../api/types.ts';
import { PRESET_LABELS, shortDate } from '../../utils/dates.ts';
import { shipLabel } from '../../utils/ships.ts';

/** The glyph on the left of a row - the same marks the plot-type picker uses. */
export const PLOT_GLYPHS: Record<string, string> = {
  line: '▤',
  scatter: '◠',
  histogram: '▥',
  heatmap: '◈',
  correlation: '▦',
  pie: '◔',
  box: '◫',
  number: '#',
  gauge: '◴',
  sankey: '⇶',
  table: '▦',
  map: '◉',
};

export function plotGlyph(plotType: string): string {
  return PLOT_GLYPHS[plotType] ?? '▤';
}

function columns(value: string | undefined): string[] {
  return (value ?? '').split(',').filter(Boolean);
}

/** "STW → PP", "PP, FuelRate", "no series" - what this chart draws. */
export function seriesSummary(params: Record<string, string>): string {
  const plotType = params.pt ?? 'line';
  if (plotType === 'table') return describeTable(new URLSearchParams(params));
  if (plotType === 'gauge') return describeGauge(new URLSearchParams(params));
  if (plotType === 'sankey') return describeSankey(new URLSearchParams(params));
  if (plotType === 'number') return params.nlabel || params.nexpr || (params.ncol ? `${(params.nagg || 'latest').toUpperCase()}(${params.ncol})` : 'no signal');
  if (plotType === 'histogram') {
    const cols = columns(params.hc);
    return cols.length ? cols.join(', ') : 'no columns';
  }
  if (plotType === 'heatmap') {
    const x = params.x ?? '?';
    const y = params.hy ?? '?';
    const z = params.hz;
    return z ? `${x} × ${y} → ${z}` : `${x} × ${y}`;
  }
  if (plotType === 'correlation') {
    const cols = columns(params.cc);
    return cols.length ? cols.join(' × ') : 'no signals';
  }
  if (plotType === 'pie') {
    if (params.pmo === 'bands') return params.pbc ? `${params.pbc} by band` : 'no signal';
    const cols = columns(params.pcol);
    return cols.length ? cols.join(' + ') : 'no signals';
  }
  if (plotType === 'box') {
    if (params.bmo === 'groups') {
      return params.bcol && params.bgrp ? `${params.bcol} by ${params.bgrp}` : 'no signal';
    }
    const cols = columns(params.bcols);
    return cols.length ? cols.join(', ') : 'no signals';
  }
  if (plotType === 'map') {
    return [params.lat, params.lon].filter(Boolean).join(' / ') || 'no position columns';
  }
  const left = columns(params.yl);
  const right = columns(params.yr);
  const series = [...left, ...right];
  if (!series.length) return 'no series';
  if (plotType === 'scatter') return `${params.x ?? '?'} → ${series.join(', ')}`;
  return series.join(', ');
}

/**
 * A template follows both viewer dimensions. A malformed legacy pin with no
 * ship also behaves as a template.
 */
export function isTemplate(chart: CatalogChart): boolean {
  return !chartPinsShip(chart) && !chartPinsTime(chart);
}

export function chartPinsShip(chart: CatalogChart): boolean {
  return chart.pin_ship ?? !!(chart.bind_context && chart.context?.ship);
}

export function chartPinsTime(chart: CatalogChart): boolean {
  return chart.pin_time ?? !!(chart.bind_context && chart.context?.ship && chart.context &&
    ['start', 'end', 'preset', 'pc'].some((key) => !!chart.context?.[key]));
}

/**
 * The ship and window a chart pinned, or the word for one that pinned none.
 *
 * `template` is deliberately plain rather than clever: it is the word the save
 * dialog and the card's chip use for the same state, so all three read as one
 * idea.
 */
export function contextSummary(chart: CatalogChart): string {
  const ship = chart.context?.ship;
  if (isTemplate(chart)) return 'template';
  const context = chart.context ?? {};
  const pc = context.pc;
  if (pc) {
    try {
      const parsed = JSON.parse(pc) as { version?: number; additional?: unknown[] };
      if (parsed.version === 1 && Array.isArray(parsed.additional)) {
        const count = Math.min(4, parsed.additional.length + 1);
        return `${chartPinsShip(chart) && ship ? shipLabel(ship) : 'viewer ships'} · ${count} periods`;
      }
    } catch {
      // Fall through to the ordinary range summary for a legacy/malformed row.
    }
  }
  const extra = columns(context.ships).filter((s) => s !== ship).length;
  const ships = chartPinsShip(chart) && ship
    ? extra ? `${shipLabel(ship)} +${extra}` : shipLabel(ship)
    : 'viewer ships';
  const preset = context.preset;
  const range = preset && preset !== 'custom'
    ? (PRESET_LABELS[preset] ?? preset)
    : context.start
      ? `${shortDate(context.start)} - ${shortDate(context.end)}`
      : 'all data';
  return `${ships} · ${chartPinsTime(chart) ? range : 'viewer time'}`;
}

/** The whole second line of a catalog row. */
export function chartSummary(chart: CatalogChart): string {
  return [contextSummary(chart), shapeSummary(chart)].join(' · ');
}

/**
 * Everything the summary says after the context: what is drawn, and how much
 * shaping sits behind it. The card shows this alone for a template, whose
 * context is already on the card as a chip.
 */
export function shapeSummary(chart: CatalogChart): string {
  const parts = [seriesSummary(chart.params)];
  const colourBy = chart.params.cb;
  if (colourBy) parts.push(`colour by ${colourBy}`);
  // `filters` is `col|op|val` rows joined by `;;`; expression mode replaces the
  // lot with one `fexpr`. Either way the row says how much shaping is hiding
  // behind the title, which is the thing people forget a chart had.
  const filters = chart.params.fexpr
    ? 1
    : (chart.params.filters ?? '').split(';;').filter(Boolean).length;
  if (filters) parts.push(filters === 1 ? '1 filter' : `${filters} filters`);
  const derived = (chart.params.d ?? '').split(',,').filter(Boolean).length;
  if (derived) parts.push(derived === 1 ? '1 derived signal' : `${derived} derived signals`);
  return parts.join(' · ');
}

/**
 * Free-text match across everything a row shows.
 *
 * The API filters on title and description; this is the client-side narrowing
 * that happens as you type, and it deliberately reaches further - a column name
 * you remember is often all you have to go on.
 */
export function chartMatches(chart: CatalogChart, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    chart.title,
    chart.description ?? '',
    chart.plot_type,
    chart.tags.join(' '),
    chart.owner.display_name,
    chartSummary(chart),
    Object.values(chart.params).join(' '),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}
